import { useEffect } from 'react';
import { ImagePlus } from 'lucide-react';
import { useEditMode } from '../../context/EditModeContext.jsx';

/**
 * Hero-Hintergrundbild (Issue #3 Paket B).
 *
 * Das Bild liegt als Backdrop HINTER der Hero-Variante (HeroSlot); die Fläche der
 * Variante wird per CSS transparent (src/index.css, [data-hero-backdrop="on"]).
 * „Stärke“ ist die Deckkraft des Bildes über weißem Grund: ein heller Schleier,
 * damit die dunklen Hero-Texte bei jedem Foto lesbar bleiben.
 */

// Live-Vorschau des Stärke-Reglers im Design-Drawer: der Regler schreibt die
// Variable direkt am <html>, ohne Netz und ohne Re-Render des Shops.
export const HERO_OPACITY_VAR = '--hero-backdrop-opacity';

export function HeroBackdrop({ imageUrl, strength = 35 }) {
  // Sobald ein neuer gespeicherter Wert (Prop) ankommt, ist die Live-Vorschau
  // überholt und wird verworfen.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.removeProperty(HERO_OPACITY_VAR);
  }, [strength, imageUrl]);

  if (!imageUrl) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="hero-backdrop"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl bg-white"
    >
      <img
        // key: neues Bild = neues Element, ein früherer onError (display:none) wirkt nicht nach.
        key={imageUrl}
        src={imageUrl}
        alt=""
        decoding="async"
        draggable={false}
        className="h-full w-full object-cover"
        style={{ opacity: `var(${HERO_OPACITY_VAR}, ${strength / 100})` }}
        onError={(event) => {
          // Objekt fehlt (z. B. gelöscht): kein kaputtes Bild-Symbol, Hero bleibt lesbar.
          event.currentTarget.style.display = 'none';
        }}
      />
    </div>
  );
}

/** „Hintergrund ändern“ am Hero — nur im Bearbeiten-Modus, öffnet den Design-Drawer. */
export function HeroBackdropEditPill() {
  const { canEdit, editMode, openDesign } = useEditMode();
  if (!canEdit || !editMode) return null;

  return (
    <button
      type="button"
      data-testid="hero-backdrop-edit"
      onClick={() => openDesign('background')}
      className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--primary)] bg-white/95 px-3 py-1.5 text-xs font-bold text-[var(--primary)] shadow-sm cursor-pointer hover:bg-white"
    >
      <ImagePlus className="h-3.5 w-3.5" />
      Hintergrund ändern
    </button>
  );
}
